import { alpha } from '@mui/material/styles';

function parseHexColor(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const h = hex.replace('#', '').trim();
  if (h.length === 3) {
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16),
    };
  }
  if (h.length !== 6) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function isPacificRimAppearance(id) {
  return String(id || '').startsWith('pr-');
}

/** @typedef {'classic' | 'hologram'} PrChromeStyle */

export const PR_CHROME_STYLE_STORAGE_KEY = 'mail-aid:prChromeStyle.v1';

/** @returns {PrChromeStyle} */
export function readPrChromeStyle() {
  try {
    const raw = localStorage.getItem(PR_CHROME_STYLE_STORAGE_KEY);
    return raw === 'classic' ? 'classic' : 'hologram';
  } catch {
    return 'hologram';
  }
}

/** @param {PrChromeStyle} style */
export function writePrChromeStyle(style) {
  try {
    if (style === 'hologram') {
      localStorage.removeItem(PR_CHROME_STYLE_STORAGE_KEY);
    } else {
      localStorage.setItem(PR_CHROME_STYLE_STORAGE_KEY, 'classic');
    }
  } catch {
    /* ignore */
  }
}

/** Hologramm-HUD nur bei PR + Style „hologram“. */
export function isHologramChrome(appearance, chromeStyle) {
  return chromeStyle === 'hologram' && isPacificRimAppearance(appearance);
}

export function hologramTextPrimary() {
  return 'rgba(232, 246, 255, 0.96)';
}

export function hologramTextSecondary() {
  return 'rgba(180, 220, 240, 0.82)';
}

/** Hologramm-Panel (Pacific Rim) aus Neon-Akzentfarbe. */
export function hologramSurfaceSx(accentHex) {
  const rgb = parseHexColor(accentHex) ?? { r: 74, g: 210, b: 255 };
  const { r, g, b } = rgb;
  return {
    backgroundColor: 'rgba(4, 10, 18, 0.78)',
    backgroundImage: `
      repeating-linear-gradient(
        0deg,
        transparent 0 2px,
        rgba(255, 255, 255, 0.035) 2px 3px
      ),
      linear-gradient(
        145deg,
        rgba(${r}, ${g}, ${b}, 0.28) 0%,
        rgba(4, 10, 18, 0.55) 42%,
        rgba(${r}, ${g}, ${b}, 0.12) 100%
      )
    `,
    border: `1px solid rgba(${r}, ${g}, ${b}, 0.72)`,
    borderRadius: 0,
    clipPath: `polygon(
      0 4px,
      4px 0,
      calc(100% - 4px) 0,
      100% 4px,
      100% calc(100% - 4px),
      calc(100% - 4px) 100%,
      4px 100%,
      0 calc(100% - 4px)
    )`,
    boxShadow: `
      0 0 8px rgba(${r}, ${g}, ${b}, 0.4),
      0 0 18px rgba(${r}, ${g}, ${b}, 0.16),
      inset 0 0 16px rgba(${r}, ${g}, ${b}, 0.08),
      inset 0 0 0 1px rgba(255, 255, 255, 0.05)
    `,
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  };
}

export function hologramContainedButtonSx(accentHex) {
  return {
    color: '#041018 !important',
    fontWeight: 700,
    borderRadius: 0,
    background: `linear-gradient(135deg, ${accentHex} 0%, ${alpha(accentHex, 0.72)} 100%)`,
    border: `1px solid ${alpha(accentHex, 0.9)}`,
    boxShadow: `0 0 14px ${alpha(accentHex, 0.45)}`,
    '&:hover': {
      background: `linear-gradient(135deg, ${alpha(accentHex, 0.95)} 0%, ${accentHex} 100%)`,
      boxShadow: `0 0 20px ${alpha(accentHex, 0.65)}`,
      color: '#041018 !important',
    },
    '&.Mui-disabled': {
      opacity: 0.55,
      color: '#041018 !important',
      background: alpha(accentHex, 0.35),
    },
  };
}

export function hologramOutlinedButtonSx(accentHex) {
  return {
    color: hologramTextPrimary(),
    borderRadius: 0,
    borderColor: alpha(accentHex, 0.55),
    bgcolor: alpha(accentHex, 0.08),
    '&:hover': {
      borderColor: accentHex,
      bgcolor: alpha(accentHex, 0.18),
      boxShadow: `0 0 12px ${alpha(accentHex, 0.35)}`,
    },
    '&.Mui-disabled': {
      color: alpha(hologramTextPrimary(), 0.35),
      borderColor: alpha(accentHex, 0.22),
    },
  };
}

export function hologramIconButtonSx(accentHex) {
  return {
    color: hologramTextPrimary(),
    borderRadius: '50%',
    border: `1px solid ${alpha(accentHex, 0.5)}`,
    bgcolor: alpha(accentHex, 0.08),
    '&:hover': {
      bgcolor: alpha(accentHex, 0.18),
      borderColor: accentHex,
      boxShadow: `0 0 10px ${alpha(accentHex, 0.35)}`,
    },
    '&.Mui-disabled': {
      color: alpha(hologramTextPrimary(), 0.35),
      borderColor: alpha(accentHex, 0.22),
      bgcolor: alpha(accentHex, 0.04),
    },
  };
}

export function hologramFieldSx(accentHex) {
  const rgb = parseHexColor(accentHex) ?? { r: 74, g: 210, b: 255 };
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      color: hologramTextPrimary(),
      backgroundColor: 'rgba(4, 10, 18, 0.55)',
      '& fieldset': {
        borderColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.45)`,
        borderRadius: 0,
      },
      '&:hover fieldset': {
        borderColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.75)`,
      },
      '&.Mui-focused fieldset': {
        borderColor: accentHex,
        boxShadow: `0 0 12px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.35)`,
      },
    },
    '& .MuiInputLabel-root': {
      color: hologramTextSecondary(),
      '&.Mui-focused': { color: accentHex },
      '&.MuiInputLabel-shrink': {
        backgroundColor: 'rgba(4, 10, 18, 0.95) !important',
      },
    },
  };
}

export function hologramTabsSx(accentHex) {
  return {
    '& .MuiTabs-indicator': {
      backgroundColor: accentHex,
      boxShadow: `0 0 8px ${alpha(accentHex, 0.7)}`,
      height: 2,
    },
    '& .MuiTab-root': {
      color: hologramTextSecondary(),
      '&.Mui-selected': {
        color: accentHex,
        textShadow: `0 0 10px ${alpha(accentHex, 0.55)}`,
      },
    },
  };
}
